import { useI18N } from "@sundae/react-hooks";
import { useWalletDetails } from "@sundae/wallet-react";
import { AssetAmount } from "@sundaeswap/asset";
import { ILockConfigArgs, YieldFarmingLucid } from "@sundaeswap/yield-farming";
import { useMutation } from "@tanstack/react-query";

import { useYieldFarmingPositionDialogContext } from "../components/YieldFarmingComponents/PositionTable/context";
import { useAppContext } from "../stores";
import { ETransactionState } from "../types/Transaction.types";
import { TransactionErrorHandler } from "../utils/transactions/TransactionErrorHandler.class";
import { useTransactionErrorHandler } from "./useTransactionErrorHandler";
import { EOrderTransactionBuilderState } from "../stores/OrderContext";

export interface IUseUpdatePositionMutationArgs {
  enabled: boolean;
  onMutationSuccess: (txHash: string) => void;
}

export const useUpdatePositionMutation = ({
  onMutationSuccess,
}: IUseUpdatePositionMutationArgs) => {
  const { t } = useI18N("transactions");
  const {
    details: { mainAddress },
  } = useWalletDetails();
  const {
    state: { sdk },
  } = useAppContext();
  const { builtTx, positionValues, setDialogState, setTransactionBuilderState, resetContext } =
    useYieldFarmingPositionDialogContext();
  const { handleTransactionError } = useTransactionErrorHandler(() =>
    setDialogState(ETransactionState.error)
  );

  const lockedValues: AssetAmount[] = Object.values(positionValues?.value ?? {}).flatMap(
    ({ assetAmount }) => assetAmount
  );

  const config: ILockConfigArgs = {
    ownerAddress: mainAddress,
    lockedValues,
    existingPositions:
      positionValues?.inputs?.map(({ txHash, index }) => ({
        hash: txHash,
        index,
      })) ?? [],
    programs: null,
  };

  const updatePositionMutation = useMutation(
    ["updateYFPositionMutation", config, builtTx],
    () => {
      return TransactionErrorHandler.handleSDKMutation(
        async () => {
          if (!sdk) {
            throw new Error("Could not find SDK instance.");
          }

          const YF = new YieldFarmingLucid(sdk.builder().lucid);
          return YF.lock(config);
        },
        builtTx,
        () => setTransactionBuilderState(EOrderTransactionBuilderState.SUCCESS)
      );
    },
    {
      cacheTime: 0,
      onMutate() {
        setTransactionBuilderState(EOrderTransactionBuilderState.BUILD);
      },
      onError({ error, transactionState, cbor }) {
        setTransactionBuilderState(EOrderTransactionBuilderState.ERROR);
        handleTransactionError(
          error,
          {
            resolutionButtonText: t("transaction.error_reset"),
            resolutionAction: () => {
              resetContext();
              setDialogState(ETransactionState.initial);
            },
          },
          transactionState,
          config,
          cbor
        );
      },
      onSuccess(txHash) {
        onMutationSuccess(txHash);
        setDialogState(ETransactionState.success);
        return txHash;
      },
    }
  );

  return {
    handleSubmitUpdatePositionTransaction: updatePositionMutation,
  };
};
